import { useGetAppsStatusQuery } from '../store/adminApi';
import { Card } from '../components/Card';
import { Table, Column } from '../components/Table';
import { ErrorState } from '../components/LoadingSpinner';
import { StatusBadge } from '../components/StatusBadge';
import type { AppStatusItem } from '../types/api';
import { CardsSkeleton, PageHeader } from '../components/admin/AdminUI';

export function AppsStatusPage() {
  const { data, isLoading, error } = useGetAppsStatusQuery(undefined, {
    pollingInterval: 60000, // Refresh every minute
  });

  const formatDate = (isoString: string | null) => {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleString();
  };

  const columns: Column<AppStatusItem>[] = [
    { key: 'appId', header: 'App ID' },
    {
      key: 'lastDispatchAt',
      header: 'Last Dispatch',
      render: (row) => formatDate(row.lastDispatchAt),
    },
    {
      key: 'dispatchCount24h',
      header: 'Dispatches (24h)',
      render: (row) => row.dispatchCount24h.toLocaleString(),
    },
    {
      key: 'failedCount24h',
      header: 'Failed (24h)',
      render: (row) => (
        <span className={row.failedCount24h > 0 ? 'text-red-600' : ''}>
          {row.failedCount24h.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'successRate',
      header: 'Success Rate (24h)',
      render: (row) => {
        if (row.dispatchCount24h === 0) {
          return <span className="text-slate-400">N/A</span>;
        }
        const rate =
          ((row.dispatchCount24h - row.failedCount24h) / row.dispatchCount24h) *
          100;
        const status =
          rate >= 95 ? 'success' : rate >= 80 ? 'warning' : 'error';
        return (
          <StatusBadge status={status} label={`${rate.toFixed(1)}%`} />
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        const lastDispatch = row.lastDispatchAt
          ? new Date(row.lastDispatchAt)
          : null;
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

        if (!lastDispatch) {
          return <StatusBadge status="neutral" label="No Activity" />;
        }
        if (lastDispatch > oneHourAgo) {
          return <StatusBadge status="success" label="Active" />;
        }
        if (lastDispatch > oneDayAgo) {
          return <StatusBadge status="info" label="Recent" />;
        }
        return <StatusBadge status="warning" label="Inactive" />;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Apps Status"
        subtitle={<>Overview of apps using LevelUp Central for push notifications</>}
      />

      <Card>
        {isLoading ? (
          <CardsSkeleton message="Loading apps status..." />
        ) : error ? (
          <ErrorState message="Failed to load apps status" />
        ) : (
          <Table<AppStatusItem>
            columns={columns}
            data={data?.apps || []}
            keyField="appId"
            emptyMessage="No apps have sent notifications yet"
          />
        )}
      </Card>
    </div>
  );
}
